import React, { useState, useRef } from "react";
import theme, { colors, RightContainerStyled } from "../../assets/styles/theme";
import { useQueryClient } from "@tanstack/react-query";
import Accordion, { AccordionSlots } from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoGoogleAds from "../../assets/images/logo-google-ads.svg";
import LogoGoogleAnalytics from "../../assets/images/logo-google-analytics.svg";
import LogoMetaAds from "../../assets/images/logo-meta-ads.svg";
import LogoLinkedinAds from "../../assets/images/logo-linkedin-ads.svg";
import LogoSimplefiAds from "../../assets/images/logo-simplifi.svg";
import { Box, CircularProgress, Link } from "@mui/material";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import { ConnectionForm } from "./ConnectionForm";

import {
  useGetDataSourcesDetails,
  useUpdateDataSourceConnection,
} from "../hooks";

interface DataSourceAccordionProps {
  dataSource: any;
  expanded: boolean;
  onExpand: () => void;
  onDelete: (
    connectionId: any,
    onSuccess: (data: any) => void,
    onError: (error: any) => void
  ) => void;
  updateError: (message: string) => void;
  updateSuccess: (dataSourceName: string) => void;
  globalEditing: boolean;
  updateCancel: () => void;
  updateEdit: () => void;
  isGetDataSourcesConnectionsCredDataPending: any;
}

export const DataSourceAccordion: React.FC<DataSourceAccordionProps> = ({
  dataSource,
  expanded,
  onExpand,
  onDelete,
  updateError,
  updateSuccess,
  globalEditing,
  updateCancel,
  updateEdit,
  isGetDataSourcesConnectionsCredDataPending,
}) => {
  const queryClient = useQueryClient();

  const [dataSourceConnectionId, SetDataSourceConnectionId] =
    React.useState("");

  const {
    data: dataSourceDetails,
    isFetching: isDataSourceDetailsLoading,
    refetch,
  } = useGetDataSourcesDetails(dataSourceConnectionId);

  const {
    mutate: updateDataSourceConnection,
    isPending: isDataSourceConnectionPending,
  } = useUpdateDataSourceConnection();

  const formRefs = React.useRef<{ [key: string]: any }>({});

  const [isEditing, setIsEditing] = useState<any>(false);
  const [isCancel, setCancel] = useState<any>(false);

  const [loader, setLoader] = useState<any>(false);
  const [submitLoader, setSubmitLoader] = useState<any>(false);

  const [connections, setConnections] = useState<any>(
    dataSource?.dataSourceConnectionDetails || []
  );

  const [expandedSection, setExpandedSection] = useState<any>(expanded);
  const [modifiedConnections, setModifiedConnections] = useState<any>({});
  const [newConnections, setNewConnections] = useState<any>([]);
  const [deletedConnections, setDeletedConnections] = useState<any>([]);

  const [error, setError] = useState<any>(false);

  const getSourceLogo = (name: any) => {
    switch (name) {
      case "Google Ads":
        return LogoGoogleAds;
      case "Google Analytics":
        return LogoGoogleAnalytics;
      case "Facebook Ads":
        return LogoMetaAds;
      case "LinkedIn Ads":
        return LogoLinkedinAds;
      case "Simpli.fi":
        return LogoSimplefiAds;
      default:
        return null;
    }
  };


  React.useEffect(() => {
    // This function will run when the visibility of the page changes
    const handleVisibilityChange = () => {
      // document.hidden is true when the page is not visible
      // document.visibilityState can be 'hidden', 'visible', or 'prerender'
      if (document.visibilityState === "visible") {
        // User has returned to our tab, reset the state
        setModifiedConnections({});
        setDeletedConnections([]);
        console.log("User returned to tab - state reset");
      }
    };

    // Add event listener for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  React.useEffect(() => {
    if (dataSourceDetails && dataSourceDetails?.data?.length > 0) {
      const transformedConnections = dataSourceDetails?.data?.map(
        (connection: any) => {
          let jsonData = {};
          try {
            if (connection.json) {
              jsonData = JSON.parse(connection.json);
            }
          } catch (e) {
            console.error("Error parsing JSON:", e);
          }

          // Create connectionJson based on data source type
          let connectionJson = {};

          if (dataSource.dataSourceName === "Google Ads") {
            connectionJson = {
              DeveloperToken: connection.token || "",
              CustomerID: connection.customerID || "",
              jsonFile: connection.json || null,
            };
          } else if (dataSource.dataSourceName === "Google Analytics") {
            connectionJson = {
              jsonFile: connection.json || null,
            };
          } else if (
            dataSource.dataSourceName === "Facebook Ads" ||
            dataSource.dataSourceName === "LinkedIn Ads"
          ) {
            connectionJson = {
              FacebookOAuthAccessToken: connection.token || "",
              LinkedInOAuthAccessToken: connection.token || "",
            };
          } else if (dataSource.dataSourceName === "Simpli.fi") {
            connectionJson = {
              SimplifiAppKey: connection.simplifiAppKey || "",
              SimplifiUserKey: connection.simplifiUserKey || "",
            };
          }

          const matchingConnectionDetail =
            dataSource.dataSourceConnectionDetails.find(
              (detail: any) =>
                detail.dataSourceConnectionId ===
                connection.dataSourceConnectionId
            );

          return {
            dataSourceConnectionId: connection.dataSourceConnectionId,
            connectionName:
              matchingConnectionDetail?.connectionName ||
              `Connection ${connection.dataSourceConnectionId}`,
            connectionJson: JSON.stringify(connectionJson),
            originalData: connection,
          };
        }
      );
      setConnections(transformedConnections);
    }
  }, [
    dataSourceDetails,
    dataSource.dataSourceName,
    isDataSourceDetailsLoading,
  ]);

  React.useEffect(() => {
    if (
      dataSource?.dataSourceConnectionDetails &&
      dataSource?.dataSourceConnectionDetails?.length > 0
    ) {
      const transformedConnections =
        dataSource?.dataSourceConnectionDetails?.map((connection: any) => {
          let jsonData: any = {};
          try {
            if (connection?.connectionJson) {
              jsonData = JSON.parse(connection?.connectionJson);
            }
          } catch (e) {
            console.error("Error parsing JSON:", e);
          }

          const matchingConnection = connections.find(
            (conn: any) =>
              conn.dataSourceConnectionId === connection.dataSourceConnectionId
          );

          let connectionJson = {};

          if (dataSource.dataSourceName === "Google Ads") {
            connectionJson = {
              DeveloperToken:
                connection?.token ||
                matchingConnection?.originalData?.token ||
                jsonData.CustomerID ||
                "",
              CustomerID:
                connection?.customerID ||
                matchingConnection?.originalData?.customerID ||
                jsonData.DeveloperToken ||
                "",
              jsonFile:
                connection?.json ||
                matchingConnection?.originalData?.json ||
                jsonData.JSON ||
                null,
            };
          } else if (dataSource.dataSourceName === "Google Analytics") {
            connectionJson = {
              jsonFile:
                connection?.json ||
                matchingConnection?.originalData?.json ||
                jsonData.JSON ||
                null,
            };
          } else if (dataSource.dataSourceName === "Facebook Ads") {
            connectionJson = {
              FacebookOAuthAccessToken:
                connection?.token ||
                matchingConnection?.originalData?.token ||
                jsonData.FacebookOAuthAccessToken ||
                "",
            };
          } else if (dataSource?.dataSourceName === "LinkedIn Ads") {
            connectionJson = {
              LinkedInOAuthAccessToken:
                connection?.token ||
                matchingConnection?.originalData?.token ||
                jsonData.LinkedInOAuthAccessToken ||
                "",
            };
          } else if (dataSource.dataSourceName === "Simpli.fi") {
            connectionJson = {
              SimplifiAppKey:
                connection?.simplifiAppKey ||
                matchingConnection?.originalData?.SimplifiAppKey ||
                jsonData.SimplifiAppKey ||
                "",
              SimplifiUserKey:
                connection?.simplifiUserKey ||
                matchingConnection?.originalData?.SimplifiUserKey ||
                jsonData.SimplifiUserKey ||
                "",
            };
          }

          const matchingConnectionDetail =
            dataSource.dataSourceConnectionDetails.find(
              (detail: any) =>
                detail.dataSourceConnectionId ===
                connection.dataSourceConnectionId
            );

          return {
            dataSourceConnectionId: connection.dataSourceConnectionId,
            connectionName:
              matchingConnectionDetail?.connectionName ||
              `Connection ${connection.dataSourceConnectionId}`,
            connectionJson: JSON.stringify(connectionJson),
            originalData: connection,
          };
        });
      setConnections(transformedConnections);
    }
  }, [dataSource?.dataSourceConnectionDetails, dataSource.dataSourceName]);

  const handleEdit = (value: any) => {
    setLoader(true);
    SetDataSourceConnectionId(value.dataSourceId);

    setTimeout(() => {
      refetch().then((response) => {
        if (response.data?.data?.length > 0) {
          const transformedConnections = response?.data?.data?.map(
            (connection: any) => {
              let jsonData = {};
              try {
                if (connection.json) {
                  jsonData = JSON.parse(connection.json);
                }
              } catch (e) {
                console.error("Error parsing JSON:", e);
              }

              let connectionJson = {};

              if (dataSource.dataSourceName === "Google Ads") {
                connectionJson = {
                  DeveloperToken: connection.token || "",
                  CustomerID: connection.customerID || "",
                  jsonFile: connection.json || null,
                };
              } else if (
                dataSource.dataSourceName === "Facebook Ads" ||
                dataSource.dataSourceName === "LinkedIn Ads"
              ) {
                connectionJson = {
                  FacebookOAuthAccessToken: connection.token || "",
                  LinkedInOAuthAccessToken: connection.token || "",
                };
              } else if (dataSource.dataSourceName === "Simpli.fi") {
                connectionJson = {
                  SimplifiAppKey: connection.simplifiAppKey || "",
                  SimplifiUserKey: connection.simplifiUserKey || "",
                };
              }

              const matchingConnectionDetail =
                dataSource.dataSourceConnectionDetails.find(
                  (detail: any) =>
                    detail.dataSourceConnectionId ===
                    connection.dataSourceConnectionId
                );

              return {
                dataSourceConnectionId: connection.dataSourceConnectionId,
                connectionName:
                  matchingConnectionDetail?.connectionName ||
                  `Connection ${connection.dataSourceConnectionId}`,
                connectionJson: JSON.stringify(connectionJson),
                originalData: connection,
              };
            }
          );
          setConnections(transformedConnections);
          setIsEditing(true);
          setLoader(false);
        }
      });
    }, 0);

    setModifiedConnections({});
    setNewConnections([]);
    setDeletedConnections([]);
  };

  const handleAddConnection = () => {
    const newConnectionId = `temp-${Date.now()}`;
    const newConnection = {
      dataSourceConnectionId: newConnectionId,
      connectionName: "",
      connectionJson: "{}",
      isNew: true,
    };

    setConnections([...connections, newConnection]);
    setNewConnections([...newConnections, newConnectionId]);
  };

  function removeId(arr: any, idToRemove: any) {
    return arr.filter((id: any) => id !== idToRemove);
  }

  const handleDeleteConnection = (connectionId: any) => {
    console.log("dele", connectionId);
    if (
      newConnections.includes(connectionId.dataSourceConnectionId) ||
      connectionId.isNew
    ) {
      setConnections(
        connections.filter(
          (id: any) =>
            id.dataSourceConnectionId !== connectionId.dataSourceConnectionId
        )
      );
      setDeletedConnections([
        ...deletedConnections,
        connectionId.dataSourceConnectionId,
      ]);

      setTimeout(() => {
        Object.values(formRefs.current).forEach(formRef => {
          if (formRef && formRef.current) {
            formRef.current.validateForm();
          }
        });
      }, 10);

     

    } else {
      onDelete(
        connectionId,
        (data: any) => {
          if (data) {
            setConnections(
              connections.filter(
                (conn: any) =>
                  conn.dataSourceConnectionId !==
                  connectionId.dataSourceConnectionId
              )
            );
            setDeletedConnections([
              ...deletedConnections,
              connectionId.dataSourceConnectionId,
            ]);
            
            setTimeout(() => {
              Object.values(formRefs.current).forEach(formRef => {
                if (formRef && formRef.current) {
                  formRef.current.validateForm();
                }
              });
            }, 10);
            
          }
        },
        (error: any) => {
          console.log("Deletion failed:", error);
        }
      );
    }
  };

  const handleErrorFormChange = (error: any) => {
    if (Object.keys(error).length > 0) {
      setError(true);
    } else {
      setError(false);
    }
  };

  const handleFormChange = (values: any, connectionId: any) => {
    if (newConnections.includes(connectionId)) {
      setModifiedConnections({
        ...modifiedConnections,
        [connectionId]: values,
      });
      return;
    }

    const originalConnection = connections.find(
      (conn: any) => conn.dataSourceConnectionId === connectionId
    );

    if (!originalConnection) {
      console.error("Original connection not found:", connectionId);
      return;
    }

    let data = JSON.parse(originalConnection.connectionJson);

    let originalValues: any;
    try {
      originalValues = originalConnection.connectionJson
        ? JSON.parse(originalConnection.connectionJson)
        : null;
    } catch (e) {
      console.error("Error parsing connection JSON:", e);
      originalValues = null;
    }

    if (originalValues) {
      originalValues = {
        connectionName: originalConnection?.connectionName,
      };

      if (dataSource.dataSourceName === "Google Ads") {
        originalValues.developerToken =
          originalConnection?.originalData?.token || data?.DeveloperToken || "";
        originalValues.customerLoginID =
          originalConnection?.originalData?.customerID ||
          data?.CustomerID ||
          "";
        originalValues.jsonFile =
          originalConnection?.originalData?.json || data?.jsonFile;
      } else if (dataSource.dataSourceName === "Google Analytics") {
        originalValues.jsonFile =
          originalConnection?.originalData?.json || data?.jsonFile;
      } else if (
        dataSource.dataSourceName === "Facebook Ads" ||
        dataSource.dataSourceName === "LinkedIn Ads"
      ) {
        originalValues.accessToken =
          originalConnection?.originalData?.token || data?.DeveloperToken || "";
      } else if (dataSource.dataSourceName === "Simpli.fi") {
        originalValues.appKey =
          originalConnection?.originalData?.simplifiAppKey ||
          data?.simplifiAppKey ||
          "";
        originalValues.userKey =
          originalConnection?.originalData?.simplifiUserKey ||
          data?.simplifiUserKey ||
          "";
      }
    }
    const hasChanged = !areObjectsEqual(values, originalValues);

    if (hasChanged) {
      setModifiedConnections({
        ...modifiedConnections,
        [connectionId]: values,
      });
    } else {
      if (modifiedConnections[connectionId]) {
        const updatedModifiedConnections = { ...modifiedConnections };
        delete updatedModifiedConnections[connectionId];
        setModifiedConnections(updatedModifiedConnections);
      }
    }
  };

  const areObjectsEqual = (obj1: any, obj2: any) => {
    if (obj1.connectionName !== obj2.connectionName) return false;

    if (dataSource.dataSourceName === "Google Ads") {
      if (obj1.developerToken !== obj2.developerToken) return false;
      if (obj1.customerLoginID !== obj2.customerLoginID) return false;
      if (obj1.jsonFile && obj1.jsonFile !== obj2.jsonFile) return false;
    } else if (dataSource.dataSourceName === "Google Analytics") {
      if (obj1.jsonFile && obj1.jsonFile !== obj2.jsonFile) return false;
    } else if (
      dataSource.dataSourceName === "Facebook Ads" ||
      dataSource.dataSourceName === "LinkedIn Ads"
    ) {
      if (obj1.accessToken !== obj2.accessToken) return false;
    } else if (dataSource.dataSourceName === "Simpli.fi") {
      if (obj1.appKey !== obj2.appKey) return false;
      if (obj1.userKey !== obj2.userKey) return false;
    }

    return true;
  };

  const handleConnectionSave = (values: any, connectionId: any) => {
    const updatedConnections = connections.map((conn: any) => {
      if (conn.dataSourceConnectionId === connectionId) {
        return {
          ...conn,
          connectionName: values.connectionName,
          connectionJson: JSON.stringify(values),
        };
      }
      return conn;
    });

    setConnections(updatedConnections);

    if (newConnections.includes(connectionId)) {
      // Already tracked as new
    } else {
      setModifiedConnections({
        ...modifiedConnections,
        [connectionId]: values,
      });
    }
  };

  const filterOutDeletedConnections = (
    modifiedConnections: any,
    deletedConnections: any
  ) =>
    Object.keys(modifiedConnections)
      .filter((key) => !deletedConnections.includes(key))
      .reduce((obj: any, key: any) => {
        obj[key] = modifiedConnections[key];
        return obj;
      }, {});

  function hasEmptyConnectionValues1(connections: any): boolean {
    return Object.values(connections).some((connection: any) => {
      return Object.values(connection).some(
        (value) =>
          value === null || (typeof value === "string" && value.trim() === "")
      );
    });
  }

  function hasInvalidConnectionsMain(
    newConnections: Record<string, any>,
    existingConnections: Array<any>
  ): boolean {
    // Check for empty/null values in new connections
    const hasEmptyValues = Object.values(newConnections).some(
      (connection: any) => {
        return Object.values(connection).some(
          (value) =>
            value === null || (typeof value === "string" && value.trim() === "")
        );
      }
    );

    // Collect all connection names from both sources
    const allConnectionNames = new Set<string>();
    let hasDuplicateNames = false;

    // Add names from new connections
    Object.values(newConnections).forEach((connection: any) => {
      if (connection.connectionName) {
        if (allConnectionNames.has(connection.connectionName)) {
          hasDuplicateNames = true;
        }
        allConnectionNames.add(connection.connectionName);
      }
    });

    // Add names from existing connections
    existingConnections.forEach((connection) => {
      const name = connection.connectionName;
      if (name) {
        if (allConnectionNames.has(name)) {
          hasDuplicateNames = true;
        }
        allConnectionNames.add(name);
      }
    });

    return hasEmptyValues || hasDuplicateNames;
  }

  function hasInvalidConnections(
    newConnections: Record<string, any>,
    existingConnections: Array<any>
  ): boolean {
    // Check for empty/null values in new connections
    const hasEmptyValues = Object.values(newConnections).some(
      (connection: any) => {
        return Object.values(connection).some(
          (value) =>
            value === null || (typeof value === "string" && value.trim() === "")
        );
      }
    );

    // Extract connection names from both new and existing connections
    const existingNames = existingConnections.map(
      (conn) => conn.connectionName
    );
    const newNames = Object.values(newConnections).map(
      (conn) => conn.connectionName
    );

    // Check for duplicates within new connections
    const uniqueNewNames = new Set(newNames);
    const hasDuplicatesInNew = uniqueNewNames.size < newNames.length;

    // Check for duplicates between new and existing connections
    let hasDuplicatesWithExisting = false;

    for (const [key, connection] of Object.entries(newConnections)) {
      const name = connection.connectionName;

      // Skip if no name provided
      if (!name) continue;

      // Check if this is an existing connection being updated
      if (!isNaN(Number(key))) {
        const existingIndex = existingConnections.findIndex(
          (conn) => conn.dataSourceConnectionId === Number(key)
        );

        // If updating an existing connection, check against other connections
        if (existingIndex >= 0) {
          // Check against other existing connections (excluding this one)
          const otherExistingNames = existingNames.filter(
            (_, idx) => idx !== existingIndex
          );
          if (otherExistingNames.includes(name)) {
            hasDuplicatesWithExisting = true;
            break;
          }

          // Also check against other new connections
          const otherNewNames = Object.entries(newConnections)
            .filter(([k, _]) => k !== key)
            .map(([_, conn]) => conn.connectionName);
          if (otherNewNames.includes(name)) {
            hasDuplicatesWithExisting = true;
            break;
          }
        }
      } else {
        // For completely new connections, check against all existing
        if (existingNames.includes(name)) {
          hasDuplicatesWithExisting = true;
          break;
        }
      }
    }

    return hasEmptyValues || hasDuplicatesInNew || hasDuplicatesWithExisting;
  }

  const handleSaveAll = async () => {
    setSubmitLoader(true);
    const updatedConnections = connections.map((conn: any) => {
      const modifiedData = modifiedConnections[conn.dataSourceConnectionId];
      if (modifiedData) {
        return {
          ...conn,
          connectionName: modifiedData.connectionName,
          connectionJson: JSON.stringify(modifiedData),
        };
      }
      return conn;
    });

    const getFileAsBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (!file) {
          resolve("");
          return;
        }

        const reader = new FileReader();

        reader.onload = () => {
          const result = reader.result as string;
          // Extract the base64 part (remove the data:application/json;base64, prefix)
          const base64String = result.split(",")[1];
          resolve(base64String);
        };

        reader.onerror = () => {
          reject(new Error("Failed to read file"));
        };

        reader.readAsDataURL(file);
      });
    };

    const prepareConnectionData = async (id: any, values: any) => {
      const baseConnection = {
        dataSourceConnectionId: id !== null && !id.includes("temp") ? id : 0,
        connectionName: values.connectionName,
      };

      if (dataSource.dataSourceName === "Google Ads") {
        const jsonBase64 =
          values.jsonFile instanceof File
            ? await getFileAsBase64(values.jsonFile)
            : btoa(values.jsonFile) || "{}";

        return {
          ...baseConnection,
          customerID: values.customerLoginID,
          token: values.developerToken,
          json: jsonBase64,
        };
      } else if (dataSource.dataSourceName === "Google Analytics") {
        const jsonBase64 =
          values.jsonFile instanceof File
            ? await getFileAsBase64(values.jsonFile)
            : btoa(values.jsonFile) || "{}";

        return {
          ...baseConnection,
          json: jsonBase64,
        };
      } else if (
        dataSource.dataSourceName === "Facebook Ads" ||
        dataSource.dataSourceName === "LinkedIn Ads"
      ) {
        return {
          ...baseConnection,
          token: values.accessToken,
        };
      } else if (dataSource.dataSourceName === "Simpli.fi") {
        return {
          ...baseConnection,
          simplifiAppKey: values.appKey,
          simplifiUserKey: values.userKey,
        };
      }

      return baseConnection;
    };

    const findConnection = (id: any) =>
      connections.find((c: any) => c.dataSourceConnectionId === id);

    const filteredOutData = filterOutDeletedConnections(
      modifiedConnections,
      deletedConnections
    );
    const modifiedData = await Promise.all(
      Object.entries(filteredOutData).map(async ([id, values]) => {
        const conn = findConnection(id);
        return await prepareConnectionData(id, values);
      })
    );

    const removeDeleted = removeId(newConnections, deletedConnections[0]);
    const newData = await Promise.all(
      Object.entries(modifiedConnections)
        .filter(([id]) => removeDeleted.includes(id))
        .map(async ([id, values]) => {
          return {
            ...(await prepareConnectionData(null, values)),
            dataSourceId: dataSource.dataSourceId,
          };
        })
    );

    filterOutDeletedConnections(modifiedConnections, deletedConnections);

    const dataToSend = {
      dataSourceId: dataSource.dataSourceId,
      modified: modifiedData,
      new: newData,
      deleted: deletedConnections,
    };

    const reqBody = {
      id: dataSource.dataSourceId,
      body: [...modifiedData],
    };

    updateDataSourceConnection(reqBody, {
      onSuccess: (res) =>
        onUpdateDataSourceConnectionSuccess(res, dataSource.dataSourceName),
      onError: onUpdateDataSourceConnectionError,
    });
  };

  const onUpdateDataSourceConnectionSuccess = (
    res: any,
    dataSourceName: any
  ) => {
    setSubmitLoader(false);
    setIsEditing(false);
    setModifiedConnections({});
    setNewConnections([]);
    setDeletedConnections([]);
    queryClient.invalidateQueries({ queryKey: ["connections-cred"] });
    updateSuccess(res?.data);
  };

  function renameFields(obj: any) {
    const newObj = { ...obj };

    if ("developerToken" in newObj) {
      newObj.DeveloperToken = newObj.developerToken;
      delete newObj.developerToken;
    }

    if ("customerLoginID" in newObj) {
      newObj.CustomerID = newObj.customerLoginID;
      delete newObj.customerLoginID;
    }

    if ("appKey" in newObj) {
      newObj.SimplifiAppKey = newObj.appKey;
      delete newObj.appKey;
    }

    if ("userKey" in newObj) {
      newObj.SimplifiUserKey = newObj.userKey;
      delete newObj.userKey;
    }

    if (dataSource.dataSourceName === "Facebook Ads") {
      if ("accessToken" in newObj) {
        newObj.FacebookOAuthAccessToken = newObj.accessToken;
        delete newObj.accessToken;
      }
    }

    if (dataSource.dataSourceName === "LinkedIn Ads") {
      if ("accessToken" in newObj) {
        newObj.LinkedInOAuthAccessToken = newObj.accessToken;
        delete newObj.accessToken;
      }
    }
    return newObj;
  }

  const onUpdateDataSourceConnectionError = (err: any) => {
    console.log("err", err);
    const previousConnections = connections.map((conn: any) => {
      const modifiedConn = renameFields(
        modifiedConnections[conn.dataSourceConnectionId]
      );
      if (modifiedConn && Object.keys(modifiedConn).length !== 0) {
        return {
          ...conn,
          connectionName: modifiedConn.connectionName,
          connectionJson: JSON.stringify(modifiedConn),
        };
      }
      return conn;
    });

    setConnections(previousConnections);

    setSubmitLoader(false);
    setModifiedConnections({});
    setNewConnections([]);
    setDeletedConnections([]);
    updateError(err?.data?.message || err?.message);
  };

  const handleCancel = () => {
    const connectionData =
      dataSource?.dataSourceConnectionDetails?.length > connections?.length
        ? connections
        : dataSource?.dataSourceConnectionDetails;
    setConnections(connectionData || []);
    setIsEditing(false);
    setCancel(true);
    updateCancel();

    setModifiedConnections({});
    setNewConnections([]);
    setDeletedConnections([]);
  };

  const accordionRef: any = useRef(null);
  return (
    <Accordion
      data-datasource-id={dataSource.dataSourceId}
      expanded={expandedSection}
      onChange={() => {
        setExpandedSection(!expandedSection);
        onExpand();
      }}
      sx={{
        "& .MuiAccordion-region": { height: expandedSection ? "auto" : 0 },
        "& .MuiAccordionDetails-root": {
          display: expandedSection ? "block" : "none",
        },
        borderBottom:
          dataSource.dataSourceName === "Simpli.fi" ? "none" : undefined,
      }}
      ref={accordionRef}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: colors.lightBlack, fontSize: "2rem" }} />
        }
        aria-controls={`panel-${dataSource.dataSourceId}-content`}
        id={`panel-${dataSource.dataSourceId}-header`}
      >
        <Typography variant="body1" className="fs-16 d-flex align-items-center">
          <img
            src={getSourceLogo(dataSource.dataSourceName) || ""}
            alt={dataSource.dataSourceName}
            className="mr-10"
          />
          {dataSource.dataSourceName}
          {!isEditing && expandedSection && (
            <Link
              sx={{ ml: 1.5 }}
              title="Edit"
              style={{ cursor: "pointer" }}
              color={"primary"}
              onClick={(e) => {
                e.stopPropagation();
                updateEdit();
                handleEdit(dataSource);
              }}
            >
              <EditIcon fontSize="small" sx={{ mt: 1 }} />
            </Link>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div id="div_id"></div>
        {connections.map((connection: any, index: any) => {
          const connectionId = connection.dataSourceConnectionId;
          formRefs.current[connectionId] =
            formRefs.current[connectionId] || React.createRef();
          return (
            <ConnectionForm
              key={connectionId}
              formRef={formRefs.current[connectionId]}
              connection={connection}
              dataSourceName={dataSource.dataSourceName}
              index={index + 1}
              isEditing={isEditing}
              onDelete={() => {
                handleDeleteConnection(connection);
              }}
              onSave={(values: any) =>
                handleConnectionSave(values, connection.dataSourceConnectionId)
              }
              onChange={(values: any) =>
                handleFormChange(values, connection.dataSourceConnectionId)
              }
              handleErrorChange={(error: any) => handleErrorFormChange(error)}
              onCancel={isCancel}
              existingConnections={connections}
              modifiedConnections={modifiedConnections}
              deletedConnections={deletedConnections}
            />
          );
        })}

        {loader && (
          <Box className="connection-loader">
            <CircularProgress size="1.5rem" sx={{ mt: 1.5, mb: 1 }} />
          </Box>
        )}

        {isEditing && (
          <Box
            className="d-flex justify-content-between cc-action-wrapper"
            sx={{ mt: 0, px: 4.5, mb: 1 }}
          >
            <Box>
              <Button
                variant="outlined"
                size="small"
                disabled={
                  isDataSourceDetailsLoading ||
                  isGetDataSourcesConnectionsCredDataPending
                }
                startIcon={<AddIcon />}
                className="btn-outlined"
                onClick={handleAddConnection}
              >
                ADD
              </Button>
            </Box>
            <Box className="d-flex justify-content-end">
              <Button variant="text" size="small" onClick={handleCancel}>
                CANCEL
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{ ml: 1, mr: 0.5 }}
                disabled={
                  submitLoader ||
                  Object.keys(
                    filterOutDeletedConnections(
                      modifiedConnections,
                      deletedConnections
                    )
                  ).length == 0 ||
                  hasInvalidConnections(
                    filterOutDeletedConnections(
                      modifiedConnections,
                      deletedConnections
                    ),
                    connections
                  )
                }
                onClick={handleSaveAll}
              >
                SAVE
                {submitLoader && (
                  <CircularProgress
                    size="1rem"
                    sx={{ color: colors.primaryDark, ml: 1 }}
                  />
                )}
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
